import { convertChannelCasing } from 'lib-shared-api-utils';
import {
  ConversationLanguageCode,
  conversationLanguages,
} from 'lib-shared-languages';
import type { Message } from 'lib-shared-legacy-types';
import type {
  ConversationChatbotMessageAddedEvent,
  ConversationEmailMessageAddedEvent,
  ConversationFacebookMessengerMessageAddedEvent,
  ConversationMessengerMessageAddedEvent,
  ConversationTwitterMessageAddedEvent,
  ConversationWhatsAppMessageAddedEvent,
  ConversationWidgetChatMessageAddedEvent,
} from 'lib-shared-types-api';
import { useContext, useEffect, useMemo } from 'react';

import { useColorMode } from 'app/utils/colorMode';
import { getMessageMarkup } from 'app/utils/messages';
import { HTMLShadowDomRenderer } from 'components/HTMLShadowDomRenderer';
import { ConversationContext } from 'components/conversation';
import { TranslateTextChangeData } from 'components/translateTextDropdownSelectionList';
import { useTranslateText } from 'utils/useTranslateText';

export type ConversationHTMLEvent =
  | ConversationEmailMessageAddedEvent
  | ConversationMessengerMessageAddedEvent
  | ConversationChatbotMessageAddedEvent
  | ConversationFacebookMessengerMessageAddedEvent
  | ConversationWhatsAppMessageAddedEvent
  | ConversationWidgetChatMessageAddedEvent
  | ConversationTwitterMessageAddedEvent;

type ConversationHTMLEventTextWithTranslationProps = {
  readonly event: ConversationHTMLEvent;
  readonly onTranslationChange?: (
    eventId: string,
    translation: TranslateTextChangeData | undefined,
  ) => void;
};

/**
 * Returns the language code of an HTML event. Not all event variants carry a
 * language field (e.g. chatbot events), so this falls back to undefined
 * safely.
 */
export const getEventLanguage = (
  event: ConversationHTMLEvent,
): ConversationLanguageCode | null | undefined =>
  (event as { language?: ConversationLanguageCode | null }).language;

/**
 * Renders a single HTML event's text with translation support from
 * ConversationContext. When a translationLanguageCode is set in context,
 * translates the message and shows a TranslationBadge.
 */
export const ConversationHTMLEventTextWithTranslation = ({
  event,
  onTranslationChange,
}: ConversationHTMLEventTextWithTranslationProps): JSX.Element | null => {
  const { colorMode } = useColorMode();
  const { translationLanguageCode, conversation } =
    useContext(ConversationContext);
  const [translate, translateResponse] = useTranslateText();

  useEffect(() => {
    if (!translationLanguageCode || !event.text) return;
    if (translationLanguageCode === getEventLanguage(event)) return;

    const lang = conversationLanguages.find((lng) =>
      translationLanguageCode.startsWith(lng.code),
    );
    if (!lang) return;

    translate({
      language: lang.name,
      text: event.text,
      csid: conversation?.id?.toString(),
      contentType: (event as { content_type?: 'html' | 'text' }).content_type,
    }).catch(() => {});
    // Only re-translate when target language changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [translationLanguageCode]);

  const translation: TranslateTextChangeData | undefined = useMemo(():
    | TranslateTextChangeData
    | undefined => {
    if (!translationLanguageCode || !event.text) return undefined;

    const selectedLanguage = conversationLanguages.find((lng) =>
      translationLanguageCode.startsWith(lng.code),
    );
    if (!selectedLanguage) return undefined;

    if (translationLanguageCode === getEventLanguage(event)) {
      return {
        originalText: event.text,
        selectedLanguage,
        status: 'notNeeded',
      };
    }

    if (translateResponse.asyncStatus === 'uninitialized') return undefined;

    return {
      originalText: event.text,
      selectedLanguage,
      translatedText: translateResponse.translatedText,
      feedbackId: translateResponse.feedbackId,
      status: translateResponse.asyncStatus,
    };
  }, [translationLanguageCode, event, translateResponse]);

  useEffect(() => {
    onTranslationChange?.(event.id, translation);
  }, [event.id, onTranslationChange, translation]);

  if (!event.text) return null;

  const { channel, ...rest } = event;
  const truncatedChannel = convertChannelCasing(channel, 'truncate');
  const displayText =
    translation?.status === 'fulfilled' && translation.translatedText
      ? translation.translatedText
      : event.text;

  return (
    <HTMLShadowDomRenderer
      html={getMessageMarkup({
        channel: truncatedChannel,
        ...rest,
        text: displayText,
      } as Message)}
      colorMode={colorMode}
    />
  );
};
